import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { resetPassword } from '@/services/api/auth';
import { useApiResponse } from '@/hooks/useApiResponse';
import { Loader2 } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { data: unwrapData } = useApiResponse();
  const [username, setUsername] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const u = params.get('user') || '';
    const k = params.get('key') || '';
    if (u) setUsername(u);
    if (k) setToken(k);
  }, [params]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !token) return;
    if (!password || password !== confirm) return;
    if (password.length < 8) return;
    setLoading(true);
    try {
      const resp = await resetPassword({ username, password, token });
      unwrapData<any>(resp, { successToast: true, errorToast: true });
      navigate('/auth');
    } catch (e) {
      // handled by unwrapData toasts
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md bg-card border rounded-lg p-6 shadow-sm">
        <h1 className="text-xl font-semibold mb-1">Set New Password</h1>
        <p className="text-sm text-muted-foreground mb-6">Enter your new password to complete the reset.</p>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="space-y-2">
            <Label htmlFor="fp-user">Username</Label>
            <Input id="fp-user" value={username} onChange={(e) => setUsername(e.target.value)} required readOnly={!!params.get('user')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fp-token">Token</Label>
            <Input id="fp-token" value={token} onChange={(e) => setToken(e.target.value)} required readOnly={!!params.get('key')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fp-pass">New Password</Label>
            <Input id="fp-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fp-confirm">Confirm Password</Label>
            <Input id="fp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update Password
          </Button>
        </form>
        <div className="text-center mt-4">
          <button className="text-sm text-muted-foreground hover:text-primary" onClick={() => navigate('/auth')}>Back to Login</button>
        </div>
      </div>
    </div>
  );
}
